import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, CATEGORIES, currencySymbol, formatAmount } from "@/lib/api";
import { toast } from "sonner";

export const DirectSplitDialog = ({ open, onOpenChange, friend, onSaved, currency = "USD" }) => {
  const [title, setTitle] = useState("");
  const [amount, setAmount] = useState("");
  const [category, setCategory] = useState("food");
  const [paidBy, setPaidBy] = useState("me"); // "me" or "friend"
  const [splitMode, setSplitMode] = useState("equal"); // "equal" or "full"
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setTitle(""); setAmount(""); setCategory("food"); setPaidBy("me"); setSplitMode("equal"); setNotes(""); }
  }, [open]);

  const sym = currencySymbol(currency);
  const share = useMemo(() => {
    const amt = Number(amount || 0);
    if (splitMode === "equal") return Math.round((amt / 2) * 100) / 100;
    return amt;
  }, [amount, splitMode]);

  const submit = async () => {
    if (!title || !amount || !friend) { toast.error("Fill title and amount"); return; }
    setSaving(true);
    try {
      await api.post("/direct-splits", {
        friend_id: friend.id, title, amount: Number(amount), currency,
        paid_by: paidBy, friend_share: share, category, notes: notes || null,
      });
      toast.success("Split added");
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  if (!friend) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-md" data-testid="direct-split-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Split with {friend.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1.5 bg-muted border-border" placeholder="Uber to airport" data-testid="ds-title" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Amount</Label>
              <div className="mt-1.5 relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 font-mono text-muted-foreground">{sym}</span>
                <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="pl-8 bg-muted border-border font-mono" placeholder="0.00" data-testid="ds-amount" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="mt-1.5 bg-muted border-border" data-testid="ds-category"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {CATEGORIES.map((c) => (<SelectItem key={c.value} value={c.value}>{c.emoji} {c.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-2 block">Paid by</Label>
            <Tabs value={paidBy} onValueChange={setPaidBy}>
              <TabsList className="bg-muted border border-border w-full grid grid-cols-2" data-testid="ds-paidby-tabs">
                <TabsTrigger value="me" className="data-[state=active]:brand-bg data-[state=active]:text-black" data-testid="ds-paidby-me">You paid</TabsTrigger>
                <TabsTrigger value="friend" className="data-[state=active]:brand-bg data-[state=active]:text-black" data-testid="ds-paidby-friend">{friend.name} paid</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-2 block">Split</Label>
            <Tabs value={splitMode} onValueChange={setSplitMode}>
              <TabsList className="bg-muted border border-border w-full grid grid-cols-2" data-testid="ds-split-tabs">
                <TabsTrigger value="equal" className="data-[state=active]:brand-bg data-[state=active]:text-black">Equal 50/50</TabsTrigger>
                <TabsTrigger value="full" className="data-[state=active]:brand-bg data-[state=active]:text-black">Full IOU</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <div className="rounded-lg border border-border bg-muted/40 p-3 text-sm flex items-center justify-between">
            <span className="text-muted-foreground font-mono uppercase tracking-wider text-xs">
              {paidBy === "me" ? `${friend.name} owes you` : `You owe ${friend.name}`}
            </span>
            <span className={`font-mono tabular-nums ${paidBy === "me" ? "brand-text" : "text-red-400"}`} data-testid="ds-share-display">
              {formatAmount(share, currency)}
            </span>
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Notes (optional)</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1.5 bg-muted border-border" data-testid="ds-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={saving} className="w-full brand-bg text-black hover:opacity-90 h-11" data-testid="ds-submit">
            {saving ? "Saving…" : "Add split"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
